import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getText } from '../i18n';
import {
  normalizeAnswer,
  getUTCDateKey,
  getDailyFlag,
  getDayNumber,
  getParisDateKey,
} from '../utils/daily';
import DailyFlagBoard from './DailyFlagBoard';
import DailyFlagInput from './DailyFlagInput';
import DailyFlagCountdown from './DailyFlagCountdown';

const MAX_ATTEMPTS = 5;
const STORAGE_PREFIX = 'dailyFlag_';
const HISTORY_KEY = 'dailyFlagHistory';

const loadDailyHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveDailyHistory = (history) => {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
};

const getDailyStorageKey = (dateKey) => `${STORAGE_PREFIX}${dateKey}`;

const loadDailyState = (dateKey) => {
  try {
    const raw = localStorage.getItem(getDailyStorageKey(dateKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function DailyFlagGame({
  allCountries,
  language,
  getLocalizedCountryNames,
  getLocalizedCountryName,
  onReturnHome,
}) {
  const dateKey = getUTCDateKey();
  const flagOfTheDay = useMemo(() => getDailyFlag(allCountries, dateKey), [allCountries, dateKey]);
  const [guessInput, setGuessInput] = useState('');
  const [dailyState, setDailyState] = useState({
    guesses: [],
    status: 'playing',
    counted: false,
  });
  const [dailySolveCount, setDailySolveCount] = useState(0);
  const [shareText, setShareText] = useState('');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const dailyInputRef = useRef(null);
  const DAILY_COUNTED_PREFIX = 'dailyFlagCounted_';

  useEffect(() => {
    const persisted = loadDailyState(dateKey);
    const counted = localStorage.getItem(`${DAILY_COUNTED_PREFIX}${dateKey}`) === '1';
    if (persisted && persisted.guesses) {
      setDailyState({ ...persisted, counted: persisted.counted || counted });
      return;
    }
    setDailyState({ guesses: [], status: 'playing', counted });
  }, [dateKey]);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [flagOfTheDay]);

  useEffect(() => {
    if (!flagOfTheDay) {
      return;
    }
    localStorage.setItem(getDailyStorageKey(dateKey), JSON.stringify(dailyState));
    if (dailyState.status !== 'playing') {
      const history = loadDailyHistory();
      history[dateKey] = {
        status: dailyState.status,
        attempts: dailyState.guesses.length,
      };
      saveDailyHistory(history);
    }
  }, [dailyState, dateKey, flagOfTheDay]);

  useEffect(() => {
    let isMounted = true;
    const fetchDailySolveCount = async () => {
      try {
        const response = await fetch(`/daily-count?date=${dateKey}`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (isMounted) {
          setDailySolveCount(data.count || 0);
        }
      } catch {
        // ignore fetch errors
      }
    };

    fetchDailySolveCount();
    return () => {
      isMounted = false;
    };
  }, [dateKey]);

  useEffect(() => {
    if (dailyState.status !== 'won') {
      return;
    }

    const hasCounted = dailyState.counted || localStorage.getItem(`${DAILY_COUNTED_PREFIX}${dateKey}`) === '1';
    if (hasCounted) {
      if (!dailyState.counted) {
        setDailyState((prevState) => ({ ...prevState, counted: true }));
      }
      return;
    }

    const incrementDailyCount = async () => {
      try {
        const response = await fetch(`/daily-count?date=${dateKey}`, {
          method: 'POST',
        });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        setDailySolveCount(data.count || 0);
        localStorage.setItem(`${DAILY_COUNTED_PREFIX}${dateKey}`, '1');
        setDailyState((prevState) => ({ ...prevState, counted: true }));
      } catch {
        // ignore post errors
      }
    };

    incrementDailyCount();
  }, [dailyState.status, dailyState.counted, dateKey]);

  const isCorrectGuess = (guess) => {
    if (!flagOfTheDay) {
      return false;
    }

    const answer = normalizeAnswer(guess);
    const acceptedNames = [flagOfTheDay.name, ...getLocalizedCountryNames(flagOfTheDay)]
      .filter(Boolean)
      .map(normalizeAnswer);

    return acceptedNames.includes(answer);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dailyState.status !== 'playing' || !guessInput.trim() || !flagOfTheDay) {
      return;
    }

    const guess = guessInput.trim();
    const correct = isCorrectGuess(guess);
    const nextGuesses = [...dailyState.guesses, { guess, correct }];
    const nextStatus = correct
      ? 'won'
      : nextGuesses.length >= MAX_ATTEMPTS
        ? 'lost'
        : 'playing';

    setDailyState({ guesses: nextGuesses, status: nextStatus, counted: dailyState.counted || false });
    setGuessInput('');
    setShareText('');
    if (nextStatus === 'playing') {
      setTimeout(() => dailyInputRef.current?.focus(), 20);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (guessInput) {
        const matches = allCountries
          .map((country) => ({
            ...country,
            displayName: getLocalizedCountryName(country) || country.name,
          }))
          .filter((country) => country.displayName.toLowerCase().startsWith(guessInput.toLowerCase()))
          .sort((a, b) => a.displayName.localeCompare(b.displayName, language));

        if (matches.length === 0) {
          return;
        }

        setGuessInput(matches[0].displayName);
      }
    }
  };

  const prevDateKey = getParisDateKey(new Date(), -1);
  const previousFlag = useMemo(() => getDailyFlag(allCountries, prevDateKey), [allCountries, prevDateKey]);
  const flagUrl = flagOfTheDay ? `${window.location.protocol}//${window.location.host}/${flagOfTheDay.flag_path}` : null;
  const prevFlagUrl = previousFlag ? `${window.location.protocol}//${window.location.host}/${previousFlag.flag_path}` : null;
  const wrongCount = dailyState.guesses.filter((row) => !row.correct).length;
  const revealStage = dailyState.status !== 'playing' ? 4 : Math.min(wrongCount, 4);
  const dayNumber = getDayNumber(dateKey);

  const handleShare = async () => {
    const resultPattern = dailyState.status === 'won'
      ? `🟩${'⬜'.repeat(MAX_ATTEMPTS - dailyState.guesses.length)}`
      : '🟥'.repeat(MAX_ATTEMPTS);
    const text = `${getText('dailyShareResult', language, {
      day: String(dayNumber),
      pattern: resultPattern,
    })}\n\nhttps://fastflagfast.com`;

    const copyText = async (value) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    };

    await copyText(text);
    setShareText(text);
  };

  if (!allCountries.length) {
    return <div className="daily-page"><p>{getText('loading', language)}</p></div>;
  }

  return (
    <div className="daily-page">
      <div className="daily-game-card">
        <div className="daily-game-header">
          <div>
            <h2>{getText('dailyFlag', language)}</h2>
            <p>{getText('dailyGameReady', language)}</p>
            <p className="daily-found-count">
              {getText('dailyFoundCount', language, { count: String(dailySolveCount) })}
            </p>
          </div>
          <DailyFlagCountdown language={language} />
        </div>

        <div className="daily-flag-preview">
          {flagUrl && (
            <div className="flag-container daily-flag-image-wrapper">
              <img
                key={flagUrl}
                src={flagUrl}
                alt={getText('dailyFlagCountdownTitle', language)}
                className="daily-flag-image"
                onLoad={() => setIsImageLoaded(true)}
                style={{ visibility: isImageLoaded ? 'visible' : 'hidden' }}
              />
              <div className={`daily-flag-mask stage-${revealStage}`} aria-hidden="true">
                <span className="corner-block top-left" />
                <span className="corner-block top-right" />
                <span className="corner-block bottom-left" />
                <span className="corner-block bottom-right" />
              </div>
            </div>
          )}
        </div>

        <div className="daily-status-row">
          <div className="daily-attempts-grid">
            {Array.from({ length: MAX_ATTEMPTS }).map((_, index) => (
              <span
                key={index}
                className={`daily-attempt-cell ${index < dailyState.guesses.length ? 'filled' : ''}`}
              />
            ))}
          </div>
        </div>

        <DailyFlagInput
          value={guessInput}
          onChange={(e) => setGuessInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onSubmit={handleSubmit}
          placeholder={getText('placeholder', language)}
          language={language}
          disabled={dailyState.status !== 'playing'}
          inputRef={dailyInputRef}
        />

        {dailyState.status !== 'playing' && (
          <div className="daily-result-panel">
            {dailyState.status === 'won' ? (
              <div className="daily-solved-card">
                <div className="daily-solved-emoji">🎉</div>
                <div className="daily-solved-copy">
                  <strong>
                    {getText('dailySolvedBadge', language, { attempts: String(dailyState.guesses.length) })}
                  </strong>
                  <span>{getText('dailySolvedSubtitle', language, { count: String(dailySolveCount) })}</span>
                </div>
              </div>
            ) : (
              <p className="daily-result-message">
                {getText('dailyCorrectAnswer', language, { name: getLocalizedCountryName(flagOfTheDay) || flagOfTheDay.name })}
              </p>
            )}
            <div className="daily-result-actions">
              <button
                type="button"
                className="daily-flag-submit"
                onClick={handleShare}
              >
                {getText('dailyShare', language)}
              </button>
            </div>
            {shareText && (
              <div className="daily-share-block">
                <div className="daily-share-block-label">
                  {getText('dailyCopied', language)}
                </div>
                <pre className="daily-share-block-content">{shareText}</pre>
              </div>
            )}
          </div>
        )}
      </div>

      {dailyState.guesses.length > 0 && (
        <DailyFlagBoard guesses={dailyState.guesses} language={language} />
      )}

      {previousFlag && prevFlagUrl && (
        <div className="daily-previous-flag">
          <div className="daily-previous-copy">
            <span className="daily-previous-label">{getText('previousFlagTitle', language)}</span>
            <span className="daily-previous-name">
              {getLocalizedCountryName(previousFlag) || previousFlag.name}
            </span>
          </div>
          <div className="daily-previous-swatch">
            <img src={prevFlagUrl} alt={getLocalizedCountryName(previousFlag) || previousFlag.name} />
          </div>
        </div>
      )}
    </div>
  );
}
